import { Device } from '@capacitor/device';

import { desktopBruecke, istGeraet } from './gleis';

/**
 * Das System, auf dem die App läuft — für Relays `platformName` und `osVersion`
 * (Pflicht bei der Anmeldung seit Relay `e964dbe`, Vorgabe des Nutzers 2026-10-14).
 *
 * Namen wie Ink (`INK/src/platform/deviceKind.ts`); im Browser mit „(Web)“ dahinter.
 */
export interface Betriebssystem {
  name: string;
  version: string;
}

/** Relay weist einen leeren Wert ab; was sich nicht ermitteln lässt, heisst so. */
const UNBEKANNT = 'unbekannt';

const DESKTOP_NAMEN: Record<string, string> = { darwin: 'macOS', win32: 'Windows', linux: 'Linux' };

export async function betriebssystem(): Promise<Betriebssystem> {
  if (istGeraet()) {
    // `osVersion` kommt aus `UIDevice.current.systemVersion` — der WKWebView gibt sich als Mac aus.
    const info = await Device.getInfo().catch(() => null);
    return { name: 'iPadOS', version: info?.osVersion || UNBEKANNT };
  }
  const bruecke = desktopBruecke();
  if (bruecke) {
    return { name: DESKTOP_NAMEN[bruecke.plattform] ?? bruecke.plattform, version: bruecke.systemVersion || UNBEKANNT };
  }
  return browserSystem();
}

type UaDaten = {
  platform?: string;
  getHighEntropyValues?: (hinweise: string[]) => Promise<{ platformVersion?: string }>;
};

async function browserSystem(): Promise<Betriebssystem> {
  const daten = (navigator as Navigator & { userAgentData?: UaDaten }).userAgentData;
  if (daten?.platform) {
    const hoch = await daten.getHighEntropyValues?.(['platformVersion']).catch(() => null);
    return { name: daten.platform + ' (Web)', version: hoch?.platformVersion || UNBEKANNT };
  }
  const aus = ausKennung(navigator.userAgent);
  return { name: aus.name + ' (Web)', version: aus.version };
}

/**
 * Safari und Firefox kennen kein `userAgentData` — dann die Browser-Kennung.
 *
 * shortcut: Safari meldet macOS eingefroren als 10.15.7, Firefox als 10.15; genauer geht es im
 * Browser nicht. Bei Bedarf die Desktop-App nehmen, die liest die echte Fassung.
 */
export function ausKennung(ua: string): Betriebssystem {
  const treffer = (re: RegExp) => re.exec(ua)?.[1]?.replace(/_/g, '.');
  if (/iPad|iPhone/.test(ua)) return { name: 'iPadOS', version: treffer(/OS (\d+[_.\d]*)/) ?? UNBEKANNT };
  if (/Android/.test(ua)) return { name: 'Android', version: treffer(/Android (\d+[.\d]*)/) ?? UNBEKANNT };
  if (/Mac OS X/.test(ua)) return { name: 'macOS', version: treffer(/Mac OS X (\d+[_.\d]*\d)/) ?? UNBEKANNT };
  if (/Windows/.test(ua)) return { name: 'Windows', version: treffer(/Windows NT (\d+[.\d]*)/) ?? UNBEKANNT };
  if (/CrOS/.test(ua)) return { name: 'Chrome OS', version: UNBEKANNT };
  if (/Linux/.test(ua)) return { name: 'Linux', version: UNBEKANNT };
  return { name: 'Browser', version: UNBEKANNT };
}

let ermittelt: Betriebssystem | null = null;

/** Einmal beim Start (`main.tsx`), vor der ersten Anmeldung — `baueClient` ist synchron. */
export async function ermittleBetriebssystem(): Promise<Betriebssystem> {
  ermittelt = await betriebssystem().catch(() => ({ name: UNBEKANNT, version: UNBEKANNT }));
  return ermittelt;
}

export function systemJetzt(): Betriebssystem {
  return ermittelt ?? { name: UNBEKANNT, version: UNBEKANNT };
}
